import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { DocumentTransmittal } from '../types';
import { documentTransmittalsApi } from '../services/api';
import DocumentSubmissionModal from '../components/DocumentTransmittals/DocumentSubmissionModal';
import './DocumentTransmittals.css';

const DocumentTransmittals: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentTransmittal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    documentType: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const documentTypes = [
    'Client Assessment Report',
    'Client IEP Report',
    'Communication Report',
    'Quarterly Report'
  ];

  const statusTypes = [
    'Processing',
    'Email Sent',
    'Email Failed',
    'Saved Only'
  ];

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        status: filters.status || undefined,
        documentType: filters.documentType || undefined
      };

      const response = await documentTransmittalsApi.getAll(params);
      setDocuments(response.data.documents);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, sortBy, sortOrder]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleDocumentSubmitted = () => {
    setIsModalOpen(false);
    fetchDocuments(); // Refresh the list
    toast.success('Document submitted successfully');
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Processing':
        return 'status-badge status-processing';
      case 'Email Sent':
        return 'status-badge status-sent';
      case 'Email Failed':
        return 'status-badge status-failed';
      case 'Saved Only':
        return 'status-badge status-saved';
      default:
        return 'status-badge';
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownload = async (documentId: number, fileName: string) => {
    try {
      await documentTransmittalsApi.download(documentId, fileName);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  return (
    <div className="document-transmittals-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Document Transmittals</h1>
          <p>Manage and track document submissions for students</p>
        </div>
        <button
          className="btn btn-primary add-document-btn"
          onClick={() => setIsModalOpen(true)}
        >
          <span className="btn-icon">+</span>
          Add New Document
        </button>
      </div>

      <div className="page-content">
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="status-filter">Status:</label>
              <select
                id="status-filter"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="">All Statuses</option>
                {statusTypes.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="type-filter">Document Type:</label>
              <select
                id="type-filter"
                value={filters.documentType}
                onChange={(e) => handleFilterChange('documentType', e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                {documentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-secondary clear-filters-btn"
              onClick={() => {
                setFilters({ status: '', documentType: '' });
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="documents-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading documents...</p>
            </div>
          ) : (
            <>
              <table className="documents-table">
                <thead>
                  <tr>
                    <th 
                      className={`sortable ${sortBy === 'student_name' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('student_name')}
                    >
                      Student Name
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'document_type' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('document_type')}
                    >
                      Document Type
                    </th>
                    <th>File Name</th>
                    <th 
                      className={`sortable ${sortBy === 'uploaded_by' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('uploaded_by')}
                    >
                      Uploaded By
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'created_at' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('created_at')}
                    >
                      Date & Time
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'status' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="no-data">
                        No documents found
                      </td>
                    </tr>
                  ) : (
                    documents.map((document: any) => (
                      <tr key={document.id}>
                        <td className="student-name">{document.student_name}</td>
                        <td className="document-type">{document.document_type}</td>
                        <td className="file-name">{document.file_name}</td>
                        <td className="uploaded-by">{document.uploaded_by}</td>
                        <td className="date-time">{formatDateTime(document.created_at)}</td>
                        <td className="status">
                          <span className={getStatusBadgeClass(document.status)}>
                            {document.status}
                          </span>
                        </td>
                        <td className="actions">
                          <button
                            className="btn btn-sm btn-outline download-btn"
                            onClick={() => handleDownload(document.id, document.file_name)}
                            title="Download document"
                          >
                            📥
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <DocumentSubmissionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleDocumentSubmitted}
      />
    </div>
  );
};

export default DocumentTransmittals;